import React, { useState, useEffect } from 'react';
import { X, Plus, Minus, Divide, Calculator, Check, AlertCircle } from 'lucide-react';
import { validateFormula, formatFormulaDisplay, getNumberColumns } from './formulaEvaluator';

/**
 * FormulaBuilder Component
 * Visual formula builder for creating calculated column formulas
 */
const FormulaBuilder = ({ 
  formula = '', 
  columns = [], 
  onChange, 
  onClose,
  isDark = false 
}) => {
  const [currentFormula, setCurrentFormula] = useState(formula);
  const [validation, setValidation] = useState({ isValid: true, error: null });
  const [previewFormula, setPreviewFormula] = useState('');

  // Get only number columns for formula
  const numberColumns = getNumberColumns(columns);

  useEffect(() => {
    setCurrentFormula(formula);
  }, [formula]);

  useEffect(() => {
    // Validate and update preview
    if (currentFormula) {
      const result = validateFormula(currentFormula, columns);
      setValidation(result);
      if (result.isValid) {
        setPreviewFormula(formatFormulaDisplay(currentFormula, columns));
      } else {
        setPreviewFormula('');
      }
    } else {
      setValidation({ isValid: true, error: null });
      setPreviewFormula('');
    }
  }, [currentFormula, columns]);

  const insertColumn = (columnKey) => {
    // Insert at cursor position or append
    setCurrentFormula(prev => {
      if (!prev) return columnKey;
      // Add space before if needed
      const needsSpace = prev.length > 0 && !/[\s+\-*/]$/.test(prev);
      return prev + (needsSpace ? ' ' : '') + columnKey;
    });
  };

  const insertOperator = (operator) => {
    setCurrentFormula(prev => {
      if (!prev) return '';
      // Add spaces around operator
      const needsSpaceBefore = prev.length > 0 && !/\s$/.test(prev);
      return prev + (needsSpaceBefore ? ' ' : '') + operator + ' ';
    });
  };

  const handleSave = () => {
    if (validation.isValid && currentFormula) {
      onChange(currentFormula);
      onClose();
    }
  };

  const handleClear = () => {
    setCurrentFormula('');
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className={`rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
        isDark ? 'bg-gray-800' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center space-x-2">
            <Calculator className="h-5 w-5 text-blue-600" />
            <h3 className={`text-lg font-semibold ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Formula Builder
            </h3>
          </div>
          <button
            onClick={onClose}
            className={`hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Column Selector */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Insert Column
            </label>
            <div className="flex flex-wrap gap-2">
              {numberColumns.length > 0 ? (
                numberColumns.map(col => (
                  <button
                    key={col.key}
                    onClick={() => insertColumn(col.key)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      isDark
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-blue-100 hover:bg-blue-200 text-blue-800'
                    }`}
                  >
                    {col.label}
                  </button>
                ))
              ) : (
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  No number columns available. Add number columns first.
                </p>
              )}
            </div>
          </div>

          {/* Operators */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Operators
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => insertOperator('+')}
                className={`flex items-center justify-center w-12 h-12 rounded-lg transition-colors ${
                  isDark
                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                }`}
                title="Addition"
              >
                <Plus className="h-5 w-5" />
              </button>
              <button
                onClick={() => insertOperator('-')}
                className={`flex items-center justify-center w-12 h-12 rounded-lg transition-colors ${
                  isDark
                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                }`}
                title="Subtraction"
              >
                <Minus className="h-5 w-5" />
              </button>
              <button
                onClick={() => insertOperator('*')}
                className={`flex items-center justify-center w-12 h-12 rounded-lg transition-colors ${
                  isDark
                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                }`}
                title="Multiplication"
              >
                <span className="text-xl font-bold">×</span>
              </button>
              <button
                onClick={() => insertOperator('/')}
                className={`flex items-center justify-center w-12 h-12 rounded-lg transition-colors ${
                  isDark
                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                }`}
                title="Division"
              >
                <Divide className="h-5 w-5" />
              </button>
              <button
                onClick={() => insertOperator('(')}
                className={`flex items-center justify-center w-12 h-12 rounded-lg transition-colors ${
                  isDark
                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                }`}
                title="Open Parenthesis"
              >
                <span className="text-xl font-bold">(</span>
              </button>
              <button
                onClick={() => insertOperator(')')}
                className={`flex items-center justify-center w-12 h-12 rounded-lg transition-colors ${
                  isDark
                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                }`}
                title="Close Parenthesis"
              >
                <span className="text-xl font-bold">)</span>
              </button>
            </div>
          </div>

          {/* Formula Input */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Formula
            </label>
            <textarea
              value={currentFormula}
              onChange={(e) => setCurrentFormula(e.target.value)}
              placeholder="Enter formula (e.g., qty * price)"
              rows={3}
              className={`w-full px-3 py-2 rounded-lg border font-mono text-sm ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            />
            <button
              onClick={handleClear}
              className={`mt-2 text-xs ${
                isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Clear formula
            </button>
          </div>

          {/* Preview */}
          {previewFormula && (
            <div className={`p-3 rounded-lg border ${
              isDark ? 'bg-gray-700 border-gray-600' : 'bg-blue-50 border-blue-200'
            }`}>
              <label className={`block text-xs font-medium mb-1 ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Preview
              </label>
              <p className={`text-sm font-mono ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                {previewFormula}
              </p>
            </div>
          )}

          {/* Validation Message */}
          {!validation.isValid && validation.error && (
            <div className={`flex items-start space-x-2 p-3 rounded-lg ${
              isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'
            }`}>
              <AlertCircle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                isDark ? 'text-red-400' : 'text-red-600'
              }`} />
              <p className={`text-sm ${
                isDark ? 'text-red-300' : 'text-red-800'
              }`}>
                {validation.error}
              </p>
            </div>
          )}

          {/* Help Text */}
          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <p className="font-medium mb-1">Formula Examples:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><code className={`px-1 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>qty * price</code> - Multiply two columns</li>
              <li><code className={`px-1 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>amount + tax</code> - Add two columns</li>
              <li><code className={`px-1 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>subtotal - discount</code> - Subtract columns</li>
              <li><code className={`px-1 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>(qty * price) + shipping</code> - Use parentheses</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className={`flex justify-end space-x-3 p-4 border-t ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              isDark
                ? 'border border-gray-600 text-gray-300 hover:bg-gray-700'
                : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!validation.isValid || !currentFormula}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              validation.isValid && currentFormula
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Check className="h-4 w-4" />
            <span>Apply Formula</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormulaBuilder;
